/**
 * ESG Market Analysis Component
 * 
 * Comprehensive market analysis dashboard providing:
 * - Real-time market trends and ESG investment flows
 * - Competitive benchmarking and peer analysis
 * - Investment analysis with sector performance metrics
 * - Market opportunity identification and assessment
 * - Regulatory impact analysis and compliance tracking
 * - Stakeholder sentiment analysis and engagement metrics
 * - Market forecasting with predictive analytics
 * - Performance tracking with key business metrics
 * 
 * Features:
 * - 8-tab interface: Overview, Trends, Competition, Investment, Opportunities, Regulation, Stakeholders, Forecast
 * - Interactive charts and visualizations for market data
 * - Real-time competitive positioning and benchmarking
 * - Comprehensive investment flow analysis and risk assessment
 * - Market opportunity identification with ROI projections
 * - Regulatory compliance tracking with impact analysis
 * - Stakeholder engagement monitoring and sentiment tracking
 * - Predictive market forecasting with scenario analysis
 * 
 * @version 1.0.0
 * @created 2025-09-23
 */

import React, { useState, useEffect } from 'react';
import esgMarketAnalysisService from '../../../services/esg/esgMarketAnalysisService';
import './ESGMarketAnalysis.css';

const ESGMarketAnalysis = () => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSector, setSelectedSector] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [forecastPeriod, setForecastPeriod] = useState('shortTerm');

  useEffect(() => {
    loadMarketData();
    
    // Subscribe to data updates
    const unsubscribe = esgMarketAnalysisService.subscribe((data) => {
      setMarketData(data);
    });

    return unsubscribe;
  }, []);

  const loadMarketData = async () => {
    try {
      setError(null);
      const data = await esgMarketAnalysisService.getMarketData();
      setMarketData(data);
    } catch (err) {
      console.error('Error loading market data:', err);
      setError('Failed to load market data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await esgMarketAnalysisService.refreshData();
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const formatCurrency = (value, currency = 'USD', unit = 'B') => {
    return `$${value}${unit} ${currency}`;
  };

  const getPerformanceColor = (value) => {
    if (value >= 20) return '#10B981';
    if (value >= 15) return '#34D399';
    if (value >= 10) return '#F59E0B';
    if (value >= 5) return '#FB923C';
    return '#EF4444';
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return '#10B981';
      case 'medium-low': return '#34D399';
      case 'medium': return '#F59E0B';
      case 'medium-high': return '#FB923C';
      case 'high': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment >= 80) return '#10B981';
    if (sentiment >= 60) return '#34D399';
    if (sentiment >= 40) return '#F59E0B';
    if (sentiment >= 20) return '#FB923C';
    return '#EF4444';
  };

  if (loading) {
    return (
      <div className="market-analysis-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading market analysis data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="market-analysis-container">
        <div className="error-message">
          <h2>Error Loading Market Data</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={loadMarketData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="market-analysis-container">
      {/* Header */}
      <div className="market-analysis-header">
        <div className="header-content">
          <div>
            <h1>ESG Market Analysis</h1>
            <p>Comprehensive market intelligence and competitive analysis for ESG business intelligence</p>
          </div>
          <div className="header-actions">
            <button 
              className="refresh-button" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tabs-container">
        <div className="tabs-nav">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'trends' ? 'active' : ''}`}
            onClick={() => setActiveTab('trends')}
          >
            Market Trends
          </button>
          <button 
            className={`tab-button ${activeTab === 'competition' ? 'active' : ''}`}
            onClick={() => setActiveTab('competition')}
          >
            Competition
          </button>
          <button 
            className={`tab-button ${activeTab === 'investment' ? 'active' : ''}`}
            onClick={() => setActiveTab('investment')}
          >
            Investment
          </button>
          <button 
            className={`tab-button ${activeTab === 'opportunities' ? 'active' : ''}`}
            onClick={() => setActiveTab('opportunities')}
          >
            Opportunities
          </button>
          <button 
            className={`tab-button ${activeTab === 'regulation' ? 'active' : ''}`}
            onClick={() => setActiveTab('regulation')}
          >
            Regulation
          </button>
          <button 
            className={`tab-button ${activeTab === 'stakeholders' ? 'active' : ''}`}
            onClick={() => setActiveTab('stakeholders')}
          >
            Stakeholders
          </button>
          <button 
            className={`tab-button ${activeTab === 'forecast' ? 'active' : ''}`}
            onClick={() => setActiveTab('forecast')}
          >
            Forecast
          </button>
        </div>

        <div className="tabs-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-content">
              <h2>Market Overview</h2>
              <p>Comprehensive view of the ESG market landscape and key performance indicators</p>
              
              <div className="overview-cards">
                <div className="market-size-card">
                  <h3>ESG Market Size</h3>
                  <div className="market-size-display">
                    <div className="market-value">{formatCurrency(marketData.marketOverview.esgMarketSize, 'USD', 'T')}</div>
                    <div className="growth-rate" style={{ color: getPerformanceColor(marketData.marketOverview.growthRate) }}>
                      +{marketData.marketOverview.growthRate}% YoY Growth
                    </div>
                  </div>
                  <div className="market-details">
                    <p><strong>Market Phase:</strong> {marketData.marketOverview.marketMaturity}</p>
                    <p><strong>Market Sentiment:</strong> {marketData.marketOverview.marketSentiment}</p>
                    <p><strong>Liquidity:</strong> {marketData.marketOverview.liquidityRating}</p>
                  </div>
                </div>

                <div className="market-stats-card">
                  <h3>Market Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <div className="stat-value">{marketData.marketOverview.totalAssets}B</div>
                      <div className="stat-label">Total Assets</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{marketData.marketOverview.activeInvestors.toLocaleString()}</div>
                      <div className="stat-label">Active Investors</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{marketData.marketOverview.sustainableFunds.toLocaleString()}</div>
                      <div className="stat-label">Sustainable Funds</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{marketData.marketOverview.volatilityIndex}</div>
                      <div className="stat-label">Volatility Index</div>
                    </div>
                  </div>
                </div>

                <div className="performance-metrics-card">
                  <h3>Key Performance Metrics</h3>
                  <div className="metrics-list">
                    {marketData.performanceMetrics.map((metric, index) => (
                      <div key={index} className="metric-item">
                        <div className="metric-info">
                          <span className="metric-name">{metric.metric}</span>
                          <span className="metric-progress">
                            <span className="metric-value" style={{ color: metric.trend === 'positive' ? '#10B981' : metric.trend === 'negative' ? '#EF4444' : '#6B7280' }}>
                              {metric.value}{metric.unit === 'percentage' ? '%' : metric.unit === 'points' ? 'pts' : metric.unit === 'months' ? 'mo' : ''}
                            </span>
                            <span className="metric-target">/{metric.target}{metric.unit === 'percentage' ? '%' : metric.unit === 'points' ? 'pts' : metric.unit === 'months' ? 'mo' : ''}</span>
                          </span>
                        </div>
                        <div className="metric-bar">
                          <div 
                            className="metric-fill" 
                            style={{ 
                              width: `${Math.min((metric.value / metric.target) * 100, 100)}%`,
                              backgroundColor: metric.trend === 'positive' ? '#10B981' : metric.trend === 'negative' ? '#EF4444' : '#6B7280'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="market-summary">
                <div className="summary-section">
                  <h3>Market Highlights</h3>
                  <ul className="highlights-list">
                    <li>ESG market continues robust growth with {marketData.marketOverview.growthRate}% year-over-year expansion</li>
                    <li>Total sustainable assets under management reached {formatCurrency(marketData.marketOverview.esgMarketSize, 'USD', 'T')}</li>
                    <li>Strong investor appetite with {marketData.marketOverview.activeInvestors.toLocaleString()} active participants</li>
                    <li>Market sentiment remains {marketData.marketOverview.marketSentiment.toLowerCase()} with high liquidity levels</li>
                    <li>Regulatory support driving continued market expansion and standardization</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Market Trends Tab */}
          {activeTab === 'trends' && (
            <div className="tab-content">
              <h2>Market Trends</h2>
              <p>Analysis of ESG investment flows, sector performance, and regional market dynamics</p>
              
              <div className="trends-section">
                <div className="investment-flow-card">
                  <h3>ESG Investment Flow Trends</h3>
                  <div className="flow-chart">
                    {marketData.marketTrends.esgInvestmentFlow.map((flow, index) => (
                      <div key={index} className="flow-period">
                        <div className="period-label">{flow.period}</div>
                        <div className="flow-bars">
                          <div className="inflow-bar" style={{ height: `${(flow.inflow / 300) * 100}px` }}>
                            <span className="flow-value">${flow.inflow}B</span>
                          </div>
                          <div className="outflow-bar" style={{ height: `${(flow.outflow / 300) * 100}px` }}>
                            <span className="flow-value">${flow.outflow}B</span>
                          </div>
                        </div>
                        <div className="net-flow" style={{ color: getPerformanceColor(flow.growthRate) }}>
                          Net: ${flow.netFlow}B ({flow.growthRate}%)
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flow-legend">
                    <div className="legend-item">
                      <div className="legend-color inflow"></div>
                      <span>Inflow</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color outflow"></div>
                      <span>Outflow</span>
                    </div>
                  </div>
                </div>

                <div className="sector-performance-card">
                  <h3>Sector Performance Analysis</h3>
                  <div className="sector-filters">
                    <select 
                      value={selectedSector} 
                      onChange={(e) => setSelectedSector(e.target.value)}
                      className="sector-filter"
                    >
                      <option value="all">All Sectors</option>
                      {marketData.marketTrends.sectorPerformance.map((sector) => (
                        <option key={sector.sector} value={sector.sector}>{sector.sector}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sectors-grid">
                    {marketData.marketTrends.sectorPerformance
                      .filter(sector => selectedSector === 'all' || sector.sector === selectedSector)
                      .map((sector, index) => (
                      <div key={index} className="sector-card">
                        <div className="sector-header">
                          <h4>{sector.sector}</h4>
                          <div className="sector-esg-score" style={{ color: getSentimentColor(sector.esgScore) }}>
                            ESG: {sector.esgScore}
                          </div>
                        </div>
                        <div className="sector-metrics">
                          <div className="metric">
                            <span className="metric-label">Performance:</span>
                            <span className="metric-value" style={{ color: getPerformanceColor(sector.performance) }}>
                              {sector.performance}%
                            </span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Market Share:</span>
                            <span className="metric-value">{sector.marketShare}%</span>
                          </div>
                          <div className="metric">
                            <span className="metric-label">Growth Rate:</span>
                            <span className="metric-value" style={{ color: getPerformanceColor(sector.growthRate) }}>
                              {sector.growthRate}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="regional-analysis-card">
                  <h3>Regional Market Analysis</h3>
                  <div className="regional-filters">
                    <select 
                      value={selectedRegion} 
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      className="region-filter"
                    >
                      <option value="all">All Regions</option>
                      {marketData.marketTrends.regionalAnalysis.map((region) => (
                        <option key={region.region} value={region.region}>{region.region}</option>
                      ))}
                    </select>
                  </div>
                  <div className="regions-grid">
                    {marketData.marketTrends.regionalAnalysis
                      .filter(region => selectedRegion === 'all' || region.region === selectedRegion)
                      .map((region, index) => (
                      <div key={index} className="region-card">
                        <div className="region-header">
                          <h4>{region.region}</h4>
                          <div className="region-share">{region.marketShare}% Market Share</div>
                        </div>
                        <div className="region-metrics">
                          <div className="metric-row">
                            <span className="metric-label">Growth Rate:</span>
                            <span className="metric-value" style={{ color: getPerformanceColor(region.growthRate) }}>
                              {region.growthRate}%
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">ESG Adoption:</span>
                            <span className="metric-value" style={{ color: getSentimentColor(region.esgAdoption) }}>
                              {region.esgAdoption}%
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Regulatory Support:</span>
                            <span className="metric-value" style={{ color: getSentimentColor(region.regulatorySupport) }}>
                              {region.regulatorySupport}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Competition Tab */}
          {activeTab === 'competition' && (
            <div className="tab-content">
              <h2>Competitive Analysis</h2>
              <p>Comprehensive competitive positioning and peer benchmarking analysis</p>
              
              <div className="competitive-overview">
                <div className="peer-comparison-card">
                  <h3>Peer Comparison Matrix</h3>
                  <div className="comparison-table">
                    <div className="table-header">
                      <div className="header-cell">Company</div>
                      <div className="header-cell">ESG Score</div>
                      <div className="header-cell">Position</div>
                      <div className="header-cell">Revenue (B)</div>
                      <div className="header-cell">Growth %</div>
                      <div className="header-cell">Market Share</div>
                      <div className="header-cell">Rating</div>
                    </div>
                    {marketData.competitiveAnalysis.peerComparison.map((company, index) => (
                      <div key={index} className={`table-row ${company.company === 'Our Company' ? 'our-company' : ''}`}>
                        <div className="table-cell company-name">{company.company}</div>
                        <div className="table-cell" style={{ color: getSentimentColor(company.esgScore) }}>
                          {company.esgScore}
                        </div>
                        <div className="table-cell">#{company.marketPosition}</div>
                        <div className="table-cell">${company.revenue}B</div>
                        <div className="table-cell" style={{ color: getPerformanceColor(company.growthRate) }}>
                          {company.growthRate}%
                        </div>
                        <div className="table-cell">{company.marketShare}%</div>
                        <div className="table-cell rating-cell">
                          <span className={`rating-badge rating-${company.sustainabilityRating.toLowerCase().replace('+', 'plus').replace('-', 'minus')}`}>
                            {company.sustainabilityRating}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="competitive-advantages-card">
                  <h3>Competitive Advantages</h3>
                  <div className="advantages-grid">
                    {marketData.competitiveAnalysis.competitiveAdvantages.map((advantage, index) => (
                      <div key={index} className="advantage-item">
                        <div className="advantage-header">
                          <h4>{advantage.category}</h4>
                          <div className="advantage-score" style={{ color: getPerformanceColor((advantage.advantage / advantage.peerAverage) * 100) }}>
                            +{advantage.advantage.toFixed(1)} pts
                          </div>
                        </div>
                        <div className="score-comparison">
                          <div className="score-bar">
                            <div className="our-score" style={{ width: `${(advantage.ourScore / 100) * 100}%` }}>
                              <span className="score-label">Our Score: {advantage.ourScore}</span>
                            </div>
                            <div className="peer-average" style={{ left: `${(advantage.peerAverage / 100) * 100}%` }}>
                              <span className="average-label">Peer Avg: {advantage.peerAverage}</span>
                            </div>
                          </div>
                        </div>
                        <div className="strength-description">
                          <p>{advantage.strength}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Investment Tab */}
          {activeTab === 'investment' && (
            <div className="tab-content">
              <h2>Investment Analysis</h2>
              <p>Comprehensive analysis of ESG investment trends, flows, and risk assessment</p>
              
              <div className="investment-overview">
                <div className="investment-trends-card">
                  <h3>ESG Investment Categories</h3>
                  <div className="investment-categories">
                    {marketData.investmentAnalysis.esgInvestmentTrends.map((category, index) => (
                      <div key={index} className="category-item">
                        <div className="category-header">
                          <h4>{category.category}</h4>
                          <div className="category-allocation">{category.allocation}%</div>
                        </div>
                        <div className="category-metrics">
                          <div className="metric-row">
                            <span className="metric-label">Growth:</span>
                            <span className="metric-value" style={{ color: getPerformanceColor(category.growth) }}>
                              {category.growth}%
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Risk Level:</span>
                            <span className="metric-value" style={{ color: getRiskLevelColor(category.riskLevel) }}>
                              {category.riskLevel}
                            </span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">Return Potential:</span>
                            <span className="metric-value">{category.returnPotential}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="investor-sentiment-card">
                  <h3>Investor Sentiment Analysis</h3>
                  <div className="sentiment-analysis">
                    <div className="institutional-sentiment">
                      <h4>Institutional Investors</h4>
                      <div className="sentiment-overview">
                        <div className="sentiment-score" style={{ color: getSentimentColor(marketData.investmentAnalysis.investorSentiment.institutional.confidence) }}>
                          {marketData.investmentAnalysis.investorSentiment.institutional.sentiment}
                        </div>
                        <div className="confidence-score">
                          Confidence: {marketData.investmentAnalysis.investorSentiment.institutional.confidence}%
                        </div>
                      </div>
                      <div className="sentiment-details">
                        <div className="detail-item">
                          <span className="detail-label">Allocation Trend:</span>
                          <span className="detail-value">{marketData.investmentAnalysis.investorSentiment.institutional.allocationTrend}</span>
                        </div>
                        <div className="concerns-section">
                          <h5>Primary Concerns:</h5>
                          <ul>
                            {marketData.investmentAnalysis.investorSentiment.institutional.primaryConcerns.map((concern, idx) => (
                              <li key={idx}>{concern}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="drivers-section">
                          <h5>Key Drivers:</h5>
                          <ul>
                            {marketData.investmentAnalysis.investorSentiment.institutional.keyDrivers.map((driver, idx) => (
                              <li key={idx}>{driver}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="retail-sentiment">
                      <h4>Retail Investors</h4>
                      <div className="sentiment-overview">
                        <div className="sentiment-score" style={{ color: getSentimentColor(marketData.investmentAnalysis.investorSentiment.retail.confidence) }}>
                          {marketData.investmentAnalysis.investorSentiment.retail.sentiment}
                        </div>
                        <div className="confidence-score">
                          Confidence: {marketData.investmentAnalysis.investorSentiment.retail.confidence}%
                        </div>
                      </div>
                      <div className="sentiment-details">
                        <div className="detail-item">
                          <span className="detail-label">Allocation Trend:</span>
                          <span className="detail-value">{marketData.investmentAnalysis.investorSentiment.retail.allocationTrend}</span>
                        </div>
                        <div className="concerns-section">
                          <h5>Primary Concerns:</h5>
                          <ul>
                            {marketData.investmentAnalysis.investorSentiment.retail.primaryConcerns.map((concern, idx) => (
                              <li key={idx}>{concern}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="drivers-section">
                          <h5>Key Drivers:</h5>
                          <ul>
                            {marketData.investmentAnalysis.investorSentiment.retail.keyDrivers.map((driver, idx) => (
                              <li key={idx}>{driver}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="risk-assessment-card">
                  <h3>Investment Risk Assessment</h3>
                  <div className="risk-overview">
                    <div className="overall-risk">
                      <h4>Overall Risk Level</h4>
                      <div className="risk-level" style={{ color: getRiskLevelColor(marketData.investmentAnalysis.riskAssessment.overallRisk) }}>
                        {marketData.investmentAnalysis.riskAssessment.overallRisk}
                      </div>
                    </div>
                    <div className="risk-breakdown">
                      <div className="risk-item">
                        <span className="risk-label">Market Risk:</span>
                        <span className="risk-value">{marketData.investmentAnalysis.riskAssessment.marketRisk}%</span>
                      </div>
                      <div className="risk-item">
                        <span className="risk-label">Regulatory Risk:</span>
                        <span className="risk-value">{marketData.investmentAnalysis.riskAssessment.regulatoryRisk}%</span>
                      </div>
                      <div className="risk-item">
                        <span className="risk-label">Operational Risk:</span>
                        <span className="risk-value">{marketData.investmentAnalysis.riskAssessment.operationalRisk}%</span>
                      </div>
                      <div className="risk-item">
                        <span className="risk-label">Reputational Risk:</span>
                        <span className="risk-value">{marketData.investmentAnalysis.riskAssessment.reputationalRisk}%</span>
                      </div>
                      <div className="risk-item">
                        <span className="risk-label">Liquidity Risk:</span>
                        <span className="risk-value">{marketData.investmentAnalysis.riskAssessment.liquidityRisk}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="mitigation-strategies">
                    <h4>Risk Mitigation Strategies</h4>
                    <ul>
                      {marketData.investmentAnalysis.riskAssessment.riskMitigationStrategies.map((strategy, index) => (
                        <li key={index}>{strategy}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Market Opportunities Tab */}
          {activeTab === 'opportunities' && (
            <div className="tab-content">
              <h2>Market Opportunities</h2>
              <p>Identified market opportunities with investment potential and strategic value</p>
              
              <div className="opportunities-grid">
                {marketData.marketOpportunities.map((opportunity, index) => (
                  <div key={index} className="opportunity-card">
                    <div className="opportunity-header">
                      <div className="opportunity-title">
                        <h3>{opportunity.title}</h3>
                        <span className="opportunity-id">{opportunity.id}</span>
                      </div>
                      <div className="opportunity-category">
                        <span className="category-badge">{opportunity.category}</span>
                      </div>
                    </div>

                    <div className="opportunity-metrics">
                      <div className="metrics-row">
                        <div className="metric-item">
                          <span className="metric-label">Market Size:</span>
                          <span className="metric-value">{formatCurrency(opportunity.marketSize)}</span>
                        </div>
                        <div className="metric-item">
                          <span className="metric-label">Investment:</span>
                          <span className="metric-value">{formatCurrency(opportunity.investmentRequired)}</span>
                        </div>
                      </div>
                      <div className="metrics-row">
                        <div className="metric-item">
                          <span className="metric-label">Expected ROI:</span>
                          <span className="metric-value" style={{ color: getPerformanceColor(opportunity.expectedROI) }}>
                            {opportunity.expectedROI}%
                          </span>
                        </div>
                        <div className="metric-item">
                          <span className="metric-label">Risk Level:</span>
                          <span className="metric-value" style={{ color: getRiskLevelColor(opportunity.riskLevel) }}>
                            {opportunity.riskLevel}
                          </span>
                        </div>
                      </div>
                      <div className="metrics-row">
                        <div className="metric-item">
                          <span className="metric-label">Growth Potential:</span>
                          <span className="metric-value">{opportunity.growthPotential}</span>
                        </div>
                        <div className="metric-item">
                          <span className="metric-label">Timeframe:</span>
                          <span className="metric-value">{opportunity.timeframe}</span>
                        </div>
                      </div>
                    </div>

                    <div className="opportunity-description">
                      <p>{opportunity.description}</p>
                    </div>

                    <div className="competitive-advantage">
                      <h4>Competitive Advantage</h4>
                      <p>{opportunity.competitiveAdvantage}</p>
                    </div>

                    <div className="success-factors">
                      <h4>Key Success Factors</h4>
                      <ul>
                        {opportunity.keySuccess.map((factor, idx) => (
                          <li key={idx}>{factor}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regulation Tab */}
          {activeTab === 'regulation' && (
            <div className="tab-content">
              <h2>Regulatory Impact Analysis</h2>
              <p>Analysis of current and upcoming regulations affecting ESG markets</p>
              
              <div className="regulation-analysis">
                <div className="current-regulations-card">
                  <h3>Current Regulations</h3>
                  <div className="regulations-list">
                    {marketData.regulatoryImpact.currentRegulations.map((regulation, index) => (
                      <div key={index} className="regulation-item">
                        <div className="regulation-header">
                          <h4>{regulation.regulation}</h4>
                          <div className="regulation-status-badges">
                            <span className={`impact-badge impact-${regulation.impact.toLowerCase().replace(' ', '-')}`}>
                              {regulation.impact} Impact
                            </span>
                            <span className={`compliance-badge ${regulation.complianceStatus.toLowerCase().replace(' ', '-')}`}>
                              {regulation.complianceStatus}
                            </span>
                          </div>
                        </div>
                        <div className="regulation-details">
                          <div className="detail-row">
                            <span className="detail-label">Market Effect:</span>
                            <span className="detail-value">{regulation.marketEffect}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Implementation Cost:</span>
                            <span className="detail-value">${regulation.implementationCost}M</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Timeline:</span>
                            <span className="detail-value">{regulation.timeline}</span>
                          </div>
                        </div>
                        <div className="business-opportunity">
                          <h5>Business Opportunity</h5>
                          <p>{regulation.businessOpportunity}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="upcoming-regulations-card">
                  <h3>Upcoming Regulations</h3>
                  <div className="upcoming-regulations-list">
                    {marketData.regulatoryImpact.upcomingRegulations.map((regulation, index) => (
                      <div key={index} className="upcoming-regulation-item">
                        <div className="regulation-header">
                          <h4>{regulation.regulation}</h4>
                          <div className="regulation-timeline">
                            <span className="timeframe">{regulation.timeframe}</span>
                          </div>
                        </div>
                        <div className="regulation-details">
                          <div className="detail-row">
                            <span className="detail-label">Expected Impact:</span>
                            <span className="detail-value">{regulation.expectedImpact}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Preparation Status:</span>
                            <span className="detail-value">{regulation.preparationStatus}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Estimated Cost:</span>
                            <span className="detail-value">${regulation.estimatedCost}M</span>
                          </div>
                        </div>
                        <div className="potential-opportunity">
                          <h5>Potential Opportunity</h5>
                          <p>{regulation.potentialOpportunity}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stakeholders Tab */}
          {activeTab === 'stakeholders' && (
            <div className="tab-content">
              <h2>Stakeholder Analysis</h2>
              <p>Comprehensive stakeholder sentiment analysis and engagement metrics</p>
              
              <div className="stakeholder-overview">
                <div className="engagement-summary-card">
                  <h3>Overall Engagement Metrics</h3>
                  <div className="summary-metrics">
                    <div className="summary-metric">
                      <div className="metric-value" style={{ color: getSentimentColor(marketData.stakeholderAnalysis.engagementMetrics.averageSentiment) }}>
                        {marketData.stakeholderAnalysis.engagementMetrics.averageSentiment}
                      </div>
                      <div className="metric-label">Average Sentiment</div>
                    </div>
                    <div className="summary-metric">
                      <div className="metric-value" style={{ color: getSentimentColor(marketData.stakeholderAnalysis.engagementMetrics.overallEngagement) }}>
                        {marketData.stakeholderAnalysis.engagementMetrics.overallEngagement}
                      </div>
                      <div className="metric-label">Overall Engagement</div>
                    </div>
                    <div className="summary-metric">
                      <div className="metric-value" style={{ color: getSentimentColor(marketData.stakeholderAnalysis.engagementMetrics.stakeholderSatisfaction) }}>
                        {marketData.stakeholderAnalysis.engagementMetrics.stakeholderSatisfaction}
                      </div>
                      <div className="metric-label">Satisfaction</div>
                    </div>
                    <div className="summary-metric">
                      <div className="metric-value" style={{ color: getSentimentColor(marketData.stakeholderAnalysis.engagementMetrics.trustIndex) }}>
                        {marketData.stakeholderAnalysis.engagementMetrics.trustIndex}
                      </div>
                      <div className="metric-label">Trust Index</div>
                    </div>
                  </div>
                </div>

                <div className="stakeholder-sentiment-card">
                  <h3>Stakeholder Sentiment Analysis</h3>
                  <div className="stakeholders-grid">
                    {marketData.stakeholderAnalysis.stakeholderSentiment.map((stakeholder, index) => (
                      <div key={index} className="stakeholder-item">
                        <div className="stakeholder-header">
                          <h4>{stakeholder.group}</h4>
                          <div className="influence-level">
                            <span className={`influence-badge influence-${stakeholder.influence.toLowerCase().replace(' ', '-')}`}>
                              {stakeholder.influence}
                            </span>
                          </div>
                        </div>
                        <div className="stakeholder-scores">
                          <div className="score-item">
                            <span className="score-label">Sentiment:</span>
                            <span className="score-value" style={{ color: getSentimentColor(stakeholder.sentiment) }}>
                              {stakeholder.sentiment}%
                            </span>
                          </div>
                          <div className="score-item">
                            <span className="score-label">Engagement:</span>
                            <span className="score-value" style={{ color: getSentimentColor(stakeholder.engagement) }}>
                              {stakeholder.engagement}%
                            </span>
                          </div>
                        </div>
                        <div className="primary-concerns">
                          <h5>Primary Concerns</h5>
                          <ul>
                            {stakeholder.primaryConcerns.map((concern, idx) => (
                              <li key={idx}>{concern}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Forecast Tab */}
          {activeTab === 'forecast' && (
            <div className="tab-content">
              <h2>Market Forecast</h2>
              <p>Predictive market analysis with short, medium, and long-term projections</p>
              
              <div className="forecast-controls">
                <div className="forecast-selector">
                  <button 
                    className={`forecast-button ${forecastPeriod === 'shortTerm' ? 'active' : ''}`}
                    onClick={() => setForecastPeriod('shortTerm')}
                  >
                    Short Term (6 months)
                  </button>
                  <button 
                    className={`forecast-button ${forecastPeriod === 'mediumTerm' ? 'active' : ''}`}
                    onClick={() => setForecastPeriod('mediumTerm')}
                  >
                    Medium Term (2 years)
                  </button>
                  <button 
                    className={`forecast-button ${forecastPeriod === 'longTerm' ? 'active' : ''}`}
                    onClick={() => setForecastPeriod('longTerm')}
                  >
                    Long Term (5 years)
                  </button>
                </div>
              </div>

              <div className="forecast-content">
                <div className="forecast-overview-card">
                  <h3>{marketData.marketForecast[forecastPeriod].period} Forecast</h3>
                  <div className="forecast-metrics">
                    <div className="forecast-metric">
                      <div className="metric-label">Market Growth</div>
                      <div className="metric-value" style={{ color: getPerformanceColor(marketData.marketForecast[forecastPeriod].marketGrowth) }}>
                        {marketData.marketForecast[forecastPeriod].marketGrowth}%
                      </div>
                    </div>
                    <div className="forecast-metric">
                      <div className="metric-label">ESG Adoption</div>
                      <div className="metric-value" style={{ color: getSentimentColor(marketData.marketForecast[forecastPeriod].esgAdoption) }}>
                        {marketData.marketForecast[forecastPeriod].esgAdoption}%
                      </div>
                    </div>
                    <div className="forecast-metric">
                      <div className="metric-label">Investment Flow</div>
                      <div className="metric-value">
                        ${marketData.marketForecast[forecastPeriod].investmentFlow}B
                      </div>
                    </div>
                  </div>
                </div>

                <div className="forecast-drivers-card">
                  <h3>Key Drivers</h3>
                  <div className="drivers-list">
                    {marketData.marketForecast[forecastPeriod].keyDrivers.map((driver, index) => (
                      <div key={index} className="driver-item">
                        <div className="driver-icon">✓</div>
                        <div className="driver-text">{driver}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="forecast-risks-card">
                  <h3>Potential Risks</h3>
                  <div className="risks-list">
                    {marketData.marketForecast[forecastPeriod].potentialRisks.map((risk, index) => (
                      <div key={index} className="risk-item">
                        <div className="risk-icon">⚠</div>
                        <div className="risk-text">{risk}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ESGMarketAnalysis;